import type { CaseStudy, CaseStudyDetails, CaseStudyHeroMetric, CaseStudyScopeItem } from '@/data/caseStudies/types';
import babelcastCaseStudy from '@/data/caseStudies/babelcast';
import crossroadsCaseStudy from '@/data/caseStudies/crossroads';
import excalibrrCaseStudy from '@/data/caseStudies/excalibrr';
import freightInvoicingCaseStudy from '@/data/caseStudies/freight_invoicing';
import narrativeCaseStudy from '@/data/caseStudies/narrative';
import straataCaseStudy from '@/data/caseStudies/straata';

import type { WorkCase, WorkCaseDetails, WorkCaseMetric, WorkCaseScopeItem } from './types';

const caseStudiesForWork: CaseStudy[] = [
  babelcastCaseStudy,
  straataCaseStudy,
  narrativeCaseStudy,
  freightInvoicingCaseStudy,
  crossroadsCaseStudy,
  excalibrrCaseStudy,
];

const impactStatements: Record<string, string> = {
  'babelcast-v1':
    'We took a one-page Word doc and, in 48 hours, prototyped a 40-feature SaaS plus brand that showed investors the market-transforming product—and it secured the founder’s funding.',
  'crossroads-v1':
    'We broke the “too big to scope” stalemate by building the integration platform that let an enterprise B2B SaaS vendor unify hundreds of Fortune 100/500 environments into one product play.',
  'freight-invoicing-platform':
    'We engineered the freight-pricing engine that codifies every contract so a refined products leader could launch as a carrier in six weeks.',
  'straata-mvp':
    'We turned a two-person consultancy into a multi-tenant payments analytics platform and investor-ready brand in five weeks.',
  'excalibrr-design-system':
    'We surgically rebuilt a 174-component design system so product, design, and engineering could ship in lockstep across every Excalibrr surface.',
  'narrative-ai-mvp':
    'We proved the market solves text-to-slides wrong by shipping a skeleton-first, data-connected AI editor in eight weeks.',
};

const mapHeroMetrics = (metrics?: CaseStudyHeroMetric[]): WorkCaseMetric[] =>
  (metrics ?? []).map(({ label, value }) => ({ label, value }));

const mapScopeOfWork = (scope?: CaseStudyScopeItem[]): WorkCaseScopeItem[] =>
  (scope ?? []).map(({ title, description }) => ({ title, description, tags: [] }));

const buildDetails = (details?: CaseStudyDetails, stackFallback: string[] = []): WorkCaseDetails | undefined => {
  if (!details) return undefined;

  const tasks = details.task ? [details.task] : [];
  const solutions = details.solution ? [details.solution] : undefined;
  const results = details.result ? [details.result] : undefined;
  const testimonial = details.testimonial
    ? {
        quote: details.testimonial.quote,
        author: details.testimonial.author,
        role: details.testimonial.role,
      }
    : undefined;

  const normalizedTasks = tasks.length
    ? tasks
    : solutions?.length
      ? solutions
      : results?.length
        ? results
        : undefined;

  return {
    tasks: normalizedTasks ?? [],
    solutions,
    results,
    testimonial,
    stack: details.stack?.length ? details.stack : stackFallback,
  };
};

const buildSummary = (study: CaseStudy): string[] => {
  const parts = [study.heroNarrative, study.caseDetails?.result, study.excerpt].filter(Boolean) as string[];
  if (parts.length > 0) {
    return parts;
  }
  return study.excerpt ? [study.excerpt] : [];
};

const inferMediaSources = (media: string, type: 'image' | 'video') => {
  if (type !== 'video') return undefined;
  const extension = media.split('.').pop()?.toLowerCase();
  const inferredType = extension === 'mov' ? 'video/quicktime' : 'video/mp4';
  return [{ src: media, type: inferredType }];
};

const toWorkCase = (study: CaseStudy): WorkCase => {
  const highlight = study.workHighlight ?? {
    id: study.id,
    mediaType: 'image' as const,
    media: study.heroImageUrl,
  };

  return {
    id: highlight.id,
    title: study.title,
    category: study.projectType ?? study.industry ?? 'Case Study',
    date: study.timeline,
    description: study.excerpt,
    headline: study.heroStatement ?? study.heroNarrative ?? study.excerpt,
    impactStatement: impactStatements[study.id],
    quote: study.caseDetails?.testimonial?.quote ?? study.heroTestimonial ?? '',
    summary: buildSummary(study),
    okrs: mapHeroMetrics(study.heroMetrics),
    scope: mapScopeOfWork(study.scopeOfWork),
    tags: study.technologies ?? [],
    mediaType: highlight.mediaType,
    media: highlight.media,
    mediaSources: highlight.mediaSources ?? inferMediaSources(highlight.media, highlight.mediaType),
    placeholder: highlight.placeholder,
    poster: highlight.poster,
    posterPlaceholder: highlight.posterPlaceholder,
    details: buildDetails(study.caseDetails, study.technologies),
    logo: study.logo,
    caseStudySlug: study.slug,
  };
};

export const workProjects: WorkCase[] = caseStudiesForWork.map(toWorkCase);

export default workProjects;
